import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link, useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/use-toast';
import LanguageSelector from '@/components/language-selector';
import { GratimyLogo } from '@/components/ui/gratimy-logo';
import { Loader2, LogIn, UserPlus, Mail, Lock, ArrowLeft, Sparkles, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

import SEO from '@/components/SEO';

export default function LoginPage() {
  const [activeTab, setActiveTab] = useState("login");
  const { user, signIn, signUp, signInWithMagicLink, resetPassword } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [gdprConsent, setGdprConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState(false);

  const [signupConfirmationSent, setSignupConfirmationSent] = useState(false);
  const [signupConfirmationEmail, setSignupConfirmationEmail] = useState('');

  // True when this page was opened as a magic-link landing target. The
  // implicit flow drops the session tokens in the URL hash; the Supabase
  // client needs a moment to parse them and establish the session. We show an
  // explicit "signing you in" state instead of leaning on the global boot
  // spinner, and bound the wait so a failure shows a clear message.
  const hasAuthCallback = () => {
    const hash = window.location.hash || '';
    return /(?:[#&]|^)(access_token|refresh_token)=/.test(hash);
  };
  const [completingSignIn, setCompletingSignIn] = useState<boolean>(() => hasAuthCallback());

  const urlParams = new URLSearchParams(window.location.search);
  const signupParam = urlParams.get('signup');
  const redirectParam = urlParams.get('redirect');

  useEffect(() => {
    if (signupParam === 'true') {
      setActiveTab('signup');
    }
  }, [signupParam]);

  // Detect a failed/expired magic link. With the implicit flow, Supabase
  // returns auth errors in the URL hash (e.g. #error=access_denied&
  // error_code=otp_expired&...). Without this, a stale/already-used link
  // would silently drop the user on the login screen with no explanation.
  useEffect(() => {
    const hasError = (raw: string) => {
      if (!raw) return false;
      const params = new URLSearchParams(raw.startsWith('#') || raw.startsWith('?') ? raw.slice(1) : raw);
      return params.has('error') || params.has('error_code') || params.has('error_description');
    };
    if (hasError(window.location.hash) || hasError(window.location.search)) {
      setActiveTab('login');
      setMagicLinkError(true);
      // Strip only the Supabase error params (and the hash) so a refresh
      // doesn't keep showing the banner, while preserving legitimate query
      // params like `redirect` and `signup`.
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete('error');
      cleaned.delete('error_code');
      cleaned.delete('error_description');
      const qs = cleaned.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  useEffect(() => {
    if (user) {
      setCompletingSignIn(false);
      if (redirectParam) {
        const decodedRedirect = decodeURIComponent(redirectParam);
        if (decodedRedirect.startsWith('/') && !decodedRedirect.startsWith('//')) {
          navigate(decodedRedirect);
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate, redirectParam]);

  // Bound the magic-link landing wait. If the session has not arrived in time
  // (e.g. a mobile in-app/incognito browser blocked the storage Supabase
  // needs), surface the friendly "link didn't work" message with a way to
  // request a fresh link — never an indefinite spinner.
  useEffect(() => {
    if (!completingSignIn) return;
    const SIGN_IN_TIMEOUT_MS = 12000;
    const timer = setTimeout(() => {
      setCompletingSignIn(false);
      setActiveTab('login');
      setMagicLinkError(true);
      // Drop the consumed/failed auth hash so a refresh starts clean.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }, SIGN_IN_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [completingSignIn]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);

    const { error } = await signInWithMagicLink(email, undefined, redirectParam || undefined);

    if (error) {
      toast({
        title: t('login_page.error_title'),
        description: error.message || t('login_page.magic_link_failed'),
        variant: "destructive",
      });
    } else {
      setMagicLinkEmail(email);
      setMagicLinkSent(true);
    }
    setIsLoading(false);
  };

  const handleResendMagicLink = async () => {
    setIsLoading(true);
    const { error } = await signInWithMagicLink(magicLinkEmail, undefined, redirectParam || undefined);
    if (error) {
      toast({
        title: t('login_page.error_title'),
        description: error.message || t('login_page.resend_failed'),
        variant: "destructive",
      });
    } else {
      toast({
        title: t('auth.magicLinkSentTitle'),
        description: t('auth.magicLinkSentDesc', { email: magicLinkEmail }),
      });
    }
    setIsLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        title: t('login_page.login_failed_title'),
        description: error.message || t('login_page.invalid_credentials'),
        variant: "destructive",
      });
      setIsLoading(false);
    } else {
      toast({
        title: t('login_page.welcome_back_title'),
        description: t('login_page.login_success'),
      });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast({
        title: t('login_page.email_required_title'),
        description: t('login_page.email_required_desc'),
        variant: "destructive",
      });
      return;
    }

    setForgotPasswordLoading(true);

    const { error } = await resetPassword(forgotPasswordEmail);

    if (error) {
      toast({
        title: t('login_page.error_title'),
        description: error.message || t('login_page.reset_link_failed'),
        variant: "destructive",
      });
    } else {
      toast({
        title: t('login_page.check_email_title'),
        description: t('login_page.check_email_desc'),
      });
      setForgotPasswordOpen(false);
      setForgotPasswordEmail('');
    }

    setForgotPasswordLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!gdprConsent) {
      toast({
        title: t('auth.consentRequired'),
        description: t('auth.consentRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const metadata = {
      gdprConsent,
      marketingConsent,
      gdprConsentTimestamp: new Date().toISOString(),
      marketingConsentTimestamp: marketingConsent ? new Date().toISOString() : null,
    };

    const { error } = await signUp(email, password, metadata);

    if (error) {
      toast({
        title: t('login_page.signup_failed_title'),
        description: error.message || t('login_page.signup_failed_desc'),
        variant: "destructive",
      });
      setIsLoading(false);
    } else {
      setSignupConfirmationEmail(email);
      setSignupConfirmationSent(true);
      setIsLoading(false);
      setEmail('');
      setPassword('');
    }
  };

  const inputClasses = "bg-white/70 border-[#BCAEF0]/50 text-[#3D1F7A] placeholder:text-[#8B5CF6]/60 focus:border-[#6B46C1] focus:ring-[#6B46C1]/30";

  if (completingSignIn) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4 px-4"
        style={{ background: 'linear-gradient(135deg, #6F8EDC 0%, #D689AC 100%)' }}
      >
        <Loader2 className="w-10 h-10 animate-spin text-white" />
        <p className="text-white/90 font-montserrat text-base text-center">
          {t('auth.signingYouIn')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #6F8EDC 0%, #D689AC 100%)',
        minHeight: '100vh',
      }}
    >
      <SEO title="Log In | Gratimy" description="Sign in or create your Gratimy account." canonicalPath="/login" noindex />
      <header className="w-full px-6 py-5">
        <div className="container mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            className="gap-2 text-white/90 hover:text-[#3D1F7A] hover:bg-white/20 rounded-full"
            onClick={() => navigate('/')}
            data-testid="button-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('app.back')}
          </Button>
          <LanguageSelector />
        </div>
      </header>

      <div className="flex-grow flex items-center justify-center p-4 pb-12">
        <div
          className="w-full max-w-lg p-8 md:p-10"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.55) 0%, rgba(244, 236, 251, 0.55) 100%)',
            backdropFilter: 'blur(24px) saturate(140%)',
            WebkitBackdropFilter: 'blur(24px) saturate(140%)',
            border: '1.5px solid rgba(255, 255, 255, 0.6)',
            borderRadius: '28px',
            boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.85), 0 16px 48px rgba(124, 159, 247, 0.22)',
          }}
        >
          <div className="text-center mb-8">
            <GratimyLogo variant="full" size={36} className="mx-auto justify-center mb-4" />
            <p className="text-[#6B46C1] text-sm font-montserrat">
              {t('app.welcome')}
            </p>
          </div>

          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={(v) => { setActiveTab(v); setMagicLinkSent(false); setShowPasswordForm(false); setSignupConfirmationSent(false); }} className="w-full">
            <TabsList
              className="auth-tabs grid w-full grid-cols-2 mb-8 rounded-full p-1"
              style={{ background: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.6)' }}
            >
              <TabsTrigger
                value="login"
                className="auth-tab-trigger rounded-full transition-all font-montserrat font-semibold"
              >
                {t('app.login')}
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                className="auth-tab-trigger rounded-full transition-all font-montserrat font-semibold"
              >
                {t('app.signup')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-0 mt-0">
              {magicLinkSent ? (
                <div className="text-center space-y-5 py-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-gradient-to-br from-[#7C9FF7] to-[#6B46C1] rounded-full border border-[#BCAEF0]/45">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl text-[#3D1F7A] mb-2 font-ztgatha font-semibold">
                      {t('auth.magicLinkSentTitle')}
                    </h3>
                    <p className="text-sm text-[#6B46C1] leading-relaxed font-montserrat">
                      {t('auth.magicLinkSentDesc', { email: magicLinkEmail })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    className="text-[#6B46C1] hover:text-[#3D1F7A] text-sm rounded-full"
                    onClick={handleResendMagicLink}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {t('auth.resendLink')}
                  </Button>
                  <div className="pt-2">
                    <button
                      className="text-xs text-[#8B5CF6] hover:text-[#6B46C1] transition-colors"
                      onClick={() => { setMagicLinkSent(false); setShowPasswordForm(true); }}
                    >
                      {t('auth.showPasswordForm')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <form onSubmit={handleMagicLink} className="space-y-5">
                    <div className="text-center">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 bg-gradient-to-br from-[#7C9FF7] to-[#6B46C1] rounded-full border border-[#BCAEF0]/45">
                          <Sparkles className="w-7 h-7 text-white" />
                        </div>
                      </div>
                      <h3 className="text-xl text-[#3D1F7A] mb-1 font-ztgatha font-semibold">
                        {magicLinkError ? t('auth.magicLinkExpiredTitle') : t('auth.magicLinkTitle')}
                      </h3>
                      <p className="text-sm text-[#6B46C1] font-montserrat">
                        {magicLinkError ? t('auth.magicLinkExpiredDesc') : t('auth.magicLinkDesc')}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="magic-email" className="flex items-center gap-2 text-[#6B46C1] text-sm">
                        <Mail className="w-4 h-4" />
                        {t('app.email')}
                      </Label>
                      <Input
                        id="magic-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        data-testid="input-login-email"
                        className={inputClasses}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full rounded-full py-3 text-base font-semibold border-0 bg-[#3B82F6] hover:bg-[#2563EB] font-montserrat"
                      disabled={isLoading}
                      data-testid="button-send-magic-link"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('auth.sending')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          {t('auth.sendMagicLink')}
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#BCAEF0]/35" />
                    </div>
                    <div className="relative flex justify-center">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 bg-transparent text-xs text-[#8B5CF6] hover:text-[#6B46C1] transition-colors px-3"
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                      >
                        {showPasswordForm ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {showPasswordForm ? t('auth.hidePasswordForm') : t('auth.orSignInWithPassword')}
                      </button>
                    </div>
                  </div>

                  {showPasswordForm && (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-password" className="flex items-center justify-between text-[#6B46C1] text-sm">
                          <span className="flex items-center gap-2">
                            <Lock className="w-4 h-4" />
                            {t('app.password')}
                          </span>
                          <button
                            type="button"
                            className="text-xs text-[#8B5CF6] hover:text-[#3D1F7A] hover:underline transition-colors"
                            onClick={() => {
                              setForgotPasswordEmail(email);
                              setForgotPasswordOpen(true);
                            }}
                            data-testid="button-forgot-password"
                          >
                            {t('app.forgot_password')}
                          </button>
                        </Label>
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isLoading}
                          data-testid="input-login-password"
                          className={inputClasses}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-full py-3 text-base font-semibold border-0 bg-[#6B46C1] hover:bg-[#553C9A] font-montserrat"
                        disabled={isLoading}
                        data-testid="button-login"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('login_page.signing_in')}
                          </>
                        ) : (
                          <>
                            <LogIn className="mr-2 h-4 w-4" />
                            {t('app.login')}
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              )}

              <div className="text-center text-sm text-[#8B5CF6] mt-6">
                <span>{t('app.no_account')} </span>
                <button
                  className="text-[#6B46C1] hover:text-[#3D1F7A] hover:underline transition-colors"
                  onClick={() => setActiveTab("signup")}
                >
                  {t('app.signup')}
                </button>
              </div>
            </TabsContent>

            <TabsContent value="signup" className="space-y-0 mt-0">
              {signupConfirmationSent ? (
                <div className="text-center space-y-5 py-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-gradient-to-br from-[#7C9FF7] to-[#6B46C1] rounded-full border border-[#BCAEF0]/45">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl text-[#3D1F7A] mb-2 font-ztgatha font-semibold">
                      {t('auth.signupConfirmTitle')}
                    </h3>
                    <p className="text-sm text-[#6B46C1] leading-relaxed font-montserrat">
                      {t('auth.signupConfirmDesc', { email: signupConfirmationEmail })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    className="text-[#6B46C1] hover:text-[#3D1F7A] text-sm rounded-full"
                    onClick={() => { setSignupConfirmationSent(false); setActiveTab('login'); }}
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    {t('app.login')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSignup} className="space-y-5">
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 bg-gradient-to-br from-[#7C9FF7] to-[#6B46C1] rounded-full border border-[#BCAEF0]/45">
                        <UserPlus className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl text-[#3D1F7A] mb-1 font-ztgatha font-semibold">
                      {t('auth.createAccount')}
                    </h3>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="flex items-center gap-2 text-[#6B46C1] text-sm">
                      <Mail className="w-4 h-4" />
                      {t('app.email')}
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="input-signup-email"
                      className={inputClasses}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="flex items-center gap-2 text-[#6B46C1] text-sm">
                      <Lock className="w-4 h-4" />
                      {t('app.password')}
                    </Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      data-testid="input-signup-password"
                      minLength={6}
                      className={inputClasses}
                    />
                    <p className="text-xs text-[#8B5CF6] font-montserrat">
                      {t('auth.passwordMinLength')}
                    </p>
                  </div>

                  <div className="space-y-4 border-t border-[#BCAEF0]/35 pt-5 mt-2">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="gdpr-consent"
                        checked={gdprConsent}
                        onCheckedChange={(checked) => setGdprConsent(checked === true)}
                        className="mt-1 border-2 border-[#6B46C1] data-[state=checked]:bg-[#6B46C1] data-[state=checked]:border-[#6B46C1]"
                        disabled={isLoading}
                        data-testid="checkbox-gdpr-consent"
                      />
                      <label htmlFor="gdpr-consent" className="text-sm text-[#6B46C1] leading-relaxed cursor-pointer">
                        {t('auth.gdprConsentLabel')}{' '}
                        <Link href="/privacy-policy" className="text-[#3D1F7A] underline hover:text-[#6B46C1]">
                          {t('legal.privacyPolicy')}
                        </Link>
                        . *
                      </label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="marketing-consent"
                        checked={marketingConsent}
                        onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                        className="mt-1 border-2 border-[#6B46C1] data-[state=checked]:bg-[#6B46C1] data-[state=checked]:border-[#6B46C1]"
                        disabled={isLoading}
                        data-testid="checkbox-marketing-consent"
                      />
                      <label htmlFor="marketing-consent" className="text-sm text-[#6B46C1] leading-relaxed cursor-pointer">
                        {t('auth.marketingConsentLabel')}
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full py-3 text-base font-semibold border-0 bg-[#6B46C1] hover:bg-[#553C9A] font-montserrat"
                    disabled={!gdprConsent || isLoading}
                    data-testid="button-create-account"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.creatingAccount')}
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        {t('auth.createAccount')}
                      </>
                    )}
                  </Button>
                </form>
              )}

              <div className="text-center text-sm text-[#8B5CF6] mt-6">
                <span>{t('app.have_account')} </span>
                <button
                  className="text-[#6B46C1] hover:text-[#3D1F7A] hover:underline transition-colors"
                  onClick={() => setActiveTab("login")}
                >
                  {t('app.login')}
                </button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="text-center text-xs text-[#8B5CF6] mt-8 pt-6 border-t border-[#BCAEF0]/30">
            <p className="font-montserrat">
              {t('login_page.secure_auth')}
            </p>
            <p className="mt-1 font-montserrat">
              {t('login_page.data_encrypted')}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent
          closeClassName="!text-[#6B46C1] hover:!text-[#3D1F7A] !bg-white/40 hover:!bg-white/60 p-1"
          className="sm:max-w-md border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.65) 0%, rgba(244, 236, 251, 0.65) 100%)',
            backdropFilter: 'blur(24px) saturate(140%)',
            WebkitBackdropFilter: 'blur(24px) saturate(140%)',
            border: '1.5px solid rgba(255, 255, 255, 0.6)',
            borderRadius: '24px',
            boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.85), 0 16px 48px rgba(124, 159, 247, 0.22)',
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-[#3D1F7A] font-ztgatha">{t('login_page.reset_dialog_title')}</DialogTitle>
            <DialogDescription className="text-[#6B46C1]">
              {t('login_page.reset_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="flex items-center gap-2 text-[#6B46C1]">
                <Mail className="w-4 h-4" />
                {t('login_page.email_address')}
              </Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                disabled={forgotPasswordLoading}
                data-testid="input-forgot-email"
                className={inputClasses}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotPasswordOpen(false)}
                disabled={forgotPasswordLoading}
                className="rounded-full border-[#BCAEF0]/60 bg-transparent text-[#6B46C1] hover:bg-white/40 hover:text-[#3D1F7A]"
              >
                {t('login_page.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={forgotPasswordLoading}
                data-testid="button-send-reset-link"
                className="rounded-full border-0 text-white bg-[#3B82F6] hover:bg-[#2563EB]"
              >
                {forgotPasswordLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('login_page.sending')}
                  </>
                ) : (
                  t('login_page.send_reset_link')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
